import { CheckSquareFilled, MinusSquareOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons'
import { HUB_PROPERTY_TYPE_ICONS, propertyTypeOptions } from '@salescore/client-common'
import {
  getReferenceByHack,
  isRequiredProperty,
  isSameNodeProperty,
  type ModelProperty,
  type NodePropertyName,
  symmetricDifferenceNodeProperties,
  type ViewConfigTreeNode,
} from '@salescore/core'
import { App, Button, Checkbox, Input, Row, Table, Tooltip } from 'antd'
import { t } from 'i18next'
import { type ReactNode, useMemo, useState } from 'react'

import type { RSheetColumn, RSheetRecordNode } from '../../../../domain/column.ts'
import { useGetModel, useGetModelAndProperty } from '../../../../recoil/view/selectors/connectionsSelector'
import { useViewConfigSheet } from '../../../../recoil/viewConfig'
import { isNodePropertyIncluded } from '../../../../rsheet/hooks/useResolveConfigFields'
import type { ReferenceToPropertyForAddChildNodeMutation } from '../findSameReferenceToChild'
import { TABLE_HEIGHT } from './const'

// eslint-disable-next-line sonarjs/super-linear-regex
const removeIdLabel = (x: string): string => x.replace(/\s*ID$/v, '')

const getFieldLabel = (property: ModelProperty): string => {
  const isRequired = isRequiredProperty(property)
  if ((property.referenceTo ?? []).length > 0) {
    const label = removeIdLabel(property.label)
    return isRequired ? `${label}(${t(`必須`)})` : label
    // return isRequired ? `${label}(参照,必須)` : `${label}(参照)`
  }
  return [property.label, isRequired ? `(${t(`必須`)})` : ''].join('')
}

export const PropertySelectorTableBody = ({
  node,
  addNodes,
  columns,
}: {
  node: ViewConfigTreeNode
  addNodes: ({
    toggleNodeProperties,
    referenceToProperty,
    option,
  }: {
    toggleNodeProperties?: NodePropertyName[]
    referenceToProperty?: ReferenceToPropertyForAddChildNodeMutation
    option?: { skipSetCurrentNodePath?: boolean }
  }) => void
  columns: Array<RSheetColumn<RSheetRecordNode>>
}): ReactNode => {
  const { name: nodeName, modelName } = node
  const [searchKey, setSearchKey] = useState('')
  const getModelAndProperty = useGetModelAndProperty()
  const getModel = useGetModel()
  const config = useViewConfigSheet()
  const { message } = App.useApp()

  const { properties, model } = useMemo(() => {
    const model = getModel(modelName)

    const properties = model?.properties.filter((property) => {
      // 過去の負債で、選択肢系プロパティには*_labelと*_valueのsuffixをもつ派生プロパティが存在している、
      // しかし、これは現在使う必要がなく、表示させないようにする
      if (
        (property.selectOptions ?? []).length > 0 &&
        (property.name.endsWith(`_label`) || property.name.endsWith(`_value`))
      ) {
        const rawName = property.name.replace(/_label$/v, '').replace(/_value$/v, '')
        const originalProperty = getModelAndProperty(modelName, rawName)
        return originalProperty === undefined // オリジナルが見つからなかったら、派生プロパティでない可能性（単純に名前に_labelが含まれているなど）があるため、表示する
      }

      return true
    })
    return {
      model,
      properties: properties ?? [],
    }
  }, [getModel, getModelAndProperty, modelName])

  /** テーブルのチェックボックスで選択中の要素のリスト（同一ノード・モデルに限定する） */
  const selectedPropertiesInTable = useMemo(() => {
    const fieldProperties = config.fields?.map((x) => x.property) ?? []
    return fieldProperties.filter((x) => x.nodeName === nodeName && x.modelName === modelName)
  }, [config.fields, modelName, nodeName])

  // 大文字小文字を区別せず部分一致で絞り込む
  const lowerSearchKey = searchKey.toLowerCase()
  const dataSource = properties.filter(
    (x) =>
      // !isIncluded(x, viewQueryFields) &&
      x.label.toLowerCase().includes(lowerSearchKey) || x.name.toLowerCase().includes(lowerSearchKey),
  )
  const requiredProperties = dataSource.filter((x) => isRequiredProperty(x))
  // まだチェックされていないプロパティ
  const uncheckedRequiredProperties = requiredProperties.filter(
    (requiredProperty) =>
      !selectedPropertiesInTable.some((x) =>
        isSameNodeProperty(x, {
          nodeName,
          modelName,
          propertyName: requiredProperty.name,
        }),
      ),
  )

  if (model === undefined) {
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO
    return <></>
  }

  /** カラムが削除可能かどうかを判定（columnDeletable === falseの場合は削除不可） */
  const isColumnDeletable = (propertyName: string): boolean => {
    const thisField = { propertyName, modelName, nodeName }
    const sheetColumn = columns.find((x) => isSameNodeProperty(x.configField?.property, thisField))
    return sheetColumn?.columnDeletable !== false
  }

  return (
    <div className="bg-white">
      <Row className="my-3" align="middle">
        <Input.Search
          autoFocus // 初期表示で検索ボックスにフォーカスする
          placeholder={t('検索')}
          data-e2e="property-selector-table-body-search"
          allowClear
          enterButton={false}
          className=""
          style={{ width: '100%' }}
          onChange={(event) => {
            setSearchKey(event.target.value)
          }}
        />
      </Row>

      <Table
        scroll={{ y: TABLE_HEIGHT - 70 }}
        dataSource={dataSource}
        pagination={{ pageSize: 100, simple: true }}
        size="small"
        rowClassName={`cursor-pointer hover:opacity-80`}
        rowSelection={{
          renderCell: (_1, record) => {
            const thisField = { propertyName: record.name, modelName, nodeName }
            const checked = isNodePropertyIncluded(thisField, selectedPropertiesInTable)
            if (!isColumnDeletable(record.name)) {
              return (
                <Tooltip title={t('この項目は削除できません')}>
                  <CheckSquareFilled style={{ color: '#d1d5db' }} />
                </Tooltip>
              )
            }
            // 表示のためだけでCheckboxを使っても、チェック時の挙動がおかしくなるため使わない
            if (checked) {
              return <CheckSquareFilled style={{ color: '#386EF8' }} />
            }
            return <MinusSquareOutlined />
          },
          selectedRowKeys: selectedPropertiesInTable.map((x) => x.propertyName),
          onChange: (names) => {
            // ヘッダー以外のチェックボックスは画像なため、ヘッダーのCheckboxのみ、onChangeが処理される
            const newProperties = names
              .filter((name) => typeof name === 'string')
              .map((name) => ({ propertyName: name, modelName, nodeName }))
            addNodes({
              toggleNodeProperties: symmetricDifferenceNodeProperties(selectedPropertiesInTable, newProperties).filter(
                (x) => isColumnDeletable(x.propertyName), // 削除不可のものはトグル処理に含めない
              ),
            })
          },
        }}
        onRow={(record) => {
          const thisField = { propertyName: record.name, modelName, nodeName }
          const sheetColumn = columns.find((x) =>
            isSameNodeProperty(x.configField?.property, {
              propertyName: record.name,
              modelName,
              nodeName,
            }),
          )
          const notDeletable = sheetColumn?.columnDeletable === false
          return {
            onClick() {
              if (notDeletable) {
                return
              }
              addNodes({
                toggleNodeProperties: [{ propertyName: record.name, modelName, nodeName }],
              })
            },
            className: isNodePropertyIncluded(thisField, selectedPropertiesInTable)
              ? // eslint-disable-next-line sonarjs/no-nested-conditional
                `bg-blue-50 ${notDeletable ? 'cursor-not-allowed' : ''}`
              : '',
          }
        }}
        rowKey={(record) => record.name}
        columns={[
          {
            key: 'propertyType',
            title: t(`型`),
            width: 70,
            filters: [
              ...propertyTypeOptions.map((x) => ({
                value: x.value,
                text: t(x.label),
              })),
              {
                value: 'reference',
                text: t(`参照`),
              },
            ],
            onFilter: (value, property) => {
              if (value === 'reference') {
                return (property.referenceTo ?? []).length > 0
              }
              return property.type === value
            },
            render(_, property) {
              const icon =
                (property.referenceTo ?? []).length > 0 ? <SearchOutlined /> : HUB_PROPERTY_TYPE_ICONS[property.type]
              return <span>{icon}</span>
            },
          },
          {
            key: 'propertyLabel',
            title: t(`項目名`),
            render(_, record) {
              return <span>{getFieldLabel(record)}</span>
            },
          },
          {
            key: 'reference',
            width: 50,
            render(_, record) {
              // このノードのプロパティが参照項目のとき、これを選択したらブロックを作成する
              if ((record.referenceTo ?? []).length > 0) {
                return (
                  <span>
                    <Button
                      // type="text"
                      size="small"
                      icon={<RightOutlined />}
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()

                        const referenceTo = getReferenceByHack(modelName, record.name, record.referenceTo)

                        if (referenceTo === undefined) {
                          message.error(t('参照先モデルが見つかりませんでした。'))
                          return
                        }

                        addNodes({
                          referenceToProperty: {
                            referenceTo,
                            propertyName: record.name,
                            modelName,
                            nodeName,
                            parentNodeName: nodeName,
                          },
                        })
                      }}
                    />
                  </span>
                )
              }
              return <></>
            },
          },
        ]}
      />
      {/* XXX: Tableのページネーションと同じ位置にボタンをおきたいが、antdのAPIだとそれが不可能なので、marginTopを操作して無理やり同じ高さにしている */}
      <div
        style={{
          height: 0,
          marginTop: -38,
          paddingLeft: 7,
        }}
      >
        <Checkbox
          // 必須項目がないとき、disabledにする
          disabled={requiredProperties.length === 0}
          // 必須項目があって、uncheckedなものがない=全てチェックしている時は、チェック済みにする
          checked={requiredProperties.length > 0 && uncheckedRequiredProperties.length === 0}
          onClick={() => {
            const addTargetProperties =
              uncheckedRequiredProperties.length > 0
                ? // 未チェックのものがあるときは、全てチェックする
                  uncheckedRequiredProperties
                : // 未チェックのものがなく、必須項目がある時、すでにチェックしたものを全て削除する
                  requiredProperties.filter((property) => {
                    const sheetColumn = columns.find((x) =>
                      isSameNodeProperty(x.configField?.property, {
                        propertyName: property.name,
                        modelName,
                        nodeName,
                      }),
                    )
                    return sheetColumn?.columnDeletable !== false
                  })

            addNodes({
              toggleNodeProperties: addTargetProperties.map((property) => ({
                propertyName: property.name,
                modelName,
                nodeName,
              })),
            })
          }}
        >
          {t(`必須項目を全て追加`)}
        </Checkbox>
      </div>
    </div>
  )
}
